/*
 * Aurelliani 2.0 - homepage + footer behaviour (own code, vanilla, no libraries).
 * Loaded as a module by the au-* homepage sections; every element is defined once.
 *   au-tabs         tabbed collection rows (au-tab-collections)
 *   au-press        press / proof logos with a quote (au-press-logos)
 *   au-coll-hover   collection names that swap the background (au-collection-hover)
 *   au-fit-text     makes one line of text fill its container width (au-footer-wordmark)
 *   au-loc          auto-submits the footer country / language form
 */

const auDefine = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

/* ---------- au-tabs ---------- */
class AuTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, false));
      tab.addEventListener('keydown', (event) => this.onKey(event, index));
    });

    this.querySelectorAll('[data-au-scroll]').forEach((button) => {
      button.addEventListener('click', () => {
        const row = this.activeRow();
        if (!row) return;
        const direction = button.dataset.auScroll === 'next' ? 1 : -1;
        row.scrollBy({ left: direction * row.clientWidth * 0.85, behavior: 'smooth' });
      });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const tab = event.target.closest('[role="tab"]');
      const index = this.tabs.indexOf(tab);
      if (index > -1) this.select(index, false);
    });
  }

  activeRow() {
    const panel = this.panels.find((item) => !item.hidden);
    return panel ? panel.querySelector('[data-au-row]') : null;
  }

  onKey(event, index) {
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.select(next, true);
  }

  select(index, focus) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      tab.classList.toggle('is-active', on);
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      if (i === index) {
        const row = panel.querySelector('[data-au-row]');
        if (row) row.scrollLeft = 0;
        panel.querySelectorAll('.au-reveal').forEach((el) => el.classList.add('is-in'));
      }
    });
    if (focus) this.tabs[index].focus();
  }
}
auDefine('au-tabs', AuTabs);

/* ---------- au-press ---------- */
class AuPress extends HTMLElement {
  connectedCallback() {
    this.logos = Array.from(this.querySelectorAll('[data-au-press-logo]'));
    this.quotes = Array.from(this.querySelectorAll('[data-au-press-quote]'));
    if (!this.logos.length || !this.quotes.length) return;

    this.logos.forEach((logo) => {
      const key = logo.dataset.auPressLogo;
      logo.addEventListener('click', () => this.show(key));
      logo.addEventListener('focus', () => this.show(key));
      logo.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') this.show(key);
      });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const logo = event.target.closest('[data-au-press-logo]');
      if (logo) this.show(logo.dataset.auPressLogo);
    });
  }

  show(key) {
    const quote = this.quotes.find((item) => item.dataset.auPressQuote === key);
    if (!quote) return;
    this.quotes.forEach((item) => {
      item.hidden = item !== quote;
    });
    this.logos.forEach((logo) => {
      const on = logo.dataset.auPressLogo === key;
      logo.classList.toggle('is-active', on);
      if (logo.tagName === 'BUTTON') logo.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
}
auDefine('au-press', AuPress);

/* ---------- au-coll-hover ---------- */
class AuCollHover extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('[data-au-ch-link]'));
    this.backgrounds = Array.from(this.querySelectorAll('[data-au-ch-bg]'));
    if (!this.links.length) return;

    this.active = 0;
    this.pointer = 'mouse';
    this.activeAtTap = 0;

    this.links.forEach((link, index) => {
      link.addEventListener('pointerdown', (event) => {
        this.pointer = event.pointerType || 'mouse';
        this.activeAtTap = this.active;
      });
      link.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') this.activate(index);
      });
      link.addEventListener('focus', () => this.activate(index));
      link.addEventListener('click', (event) => {
        // Touch: the first tap only selects the name, the second tap opens it.
        if (this.pointer === 'touch' && this.activeAtTap !== index) {
          event.preventDefault();
          this.activate(index);
          this.activeAtTap = index;
        }
        this.pointer = 'mouse';
      });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const link = event.target.closest('[data-au-ch-link]');
      const index = this.links.indexOf(link);
      if (index > -1) this.activate(index);
    });

    this.activate(0);
  }

  activate(index) {
    this.active = index;
    this.links.forEach((link, i) => {
      link.classList.toggle('is-active', i === index);
      if (i === index) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    this.backgrounds.forEach((background, i) => {
      background.classList.toggle('is-active', i === index);
    });
    // White text only over a photo; light backgrounds keep the dark ink.
    const current = this.backgrounds[index];
    this.classList.toggle('is-photo', Boolean(current && current.dataset.photo === 'true'));
  }
}
auDefine('au-coll-hover', AuCollHover);

/* ---------- au-fit-text ---------- */
class AuFitText extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('[data-au-fit]');
    if (!this.text) return;
    this.lastWidth = 0;
    this.fit = this.fit.bind(this);
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.fit());
      this.observer.observe(this);
    } else {
      window.addEventListener('resize', this.fit);
    }
    if (document.fonts) {
      if (document.fonts.ready) document.fonts.ready.then(() => this.fit(true));
      /* the heading font can arrive after the first fit (it was measured in the fallback font) */
      if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', () => this.fit(true));
    }
    this.fit(true);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.fit);
  }

  fit(force) {
    const width = this.clientWidth;
    if (!width || (force !== true && width === this.lastWidth)) return;
    this.lastWidth = width;
    const text = this.text;
    text.style.fontSize = '100px';
    const inkWidth = () => { const r = document.createRange(); r.selectNodeContents(text); return r.getBoundingClientRect().width; };
    const natural = inkWidth();
    if (!natural) return;
    let size = Math.floor((100 * width * 0.99) / natural * 100) / 100;
    text.style.fontSize = `${size}px`;
    /* glyph hinting does not scale exactly: shrink a little more until every letter fits */
    for (let i = 0; i < 4; i++) {
      const w = inkWidth();
      if (w <= width) break;
      size = Math.floor(size * (width / w) * 0.995 * 100) / 100;
      text.style.fontSize = `${size}px`;
    }
    this.classList.add('is-fitted');
  }
}
auDefine('au-fit-text', AuFitText);

/* ---------- au-loc ---------- */
class AuLoc extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      if (event.target.tagName !== 'SELECT') return;
      const form = this.querySelector('form');
      if (form) form.submit();
    });
  }
}
auDefine('au-loc', AuLoc);
